import { afterNextRender, Component } from '@angular/core';
import { Auth, FacebookAuthProvider, GoogleAuthProvider, OAuthProvider, signInWithPopup } from '@angular/fire/auth';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { APP_PROD_VERSION, APP_VERSION } from '../../../../../core/constants/app.constant';
import { VALID_EMAIL } from '../../../../../core/constants/general.constant';
import { EnterInfoPopupComponent } from '../../../../../core/libraries/popup/components/enter-info-popup/enter-info-popup.component';
import { SuccessPopupComponent } from '../../../../../core/libraries/popup/components/success-popup/success-popup.component';
import { ILoginData } from '../../../../../core/models/auth.model';
import { AuthService } from '../../../../../core/services/api/auth/auth.service';
import { UserService } from '../../../../../core/services/api/user/user.service';
import { LoaderService } from '../../../../../core/services/loader/loader.service';
import { StorageService } from '../../../../../core/services/storage/storage.service';

@Component({
    selector: 'app-landing',
    standalone: false,
    templateUrl: './landing.component.html',
    styleUrl: './landing.component.scss',
})
export class LandingComponent {
    protected APP_VERSION = APP_VERSION;
    protected APP_PROD_VERSION = APP_PROD_VERSION;
    protected authForm = this.fb.group({
        email: ['', [Validators.required, Validators.pattern(VALID_EMAIL)]],
        isAgree: [true, [Validators.requiredTrue]],
    });
    protected envName = environment.name;
    protected token = this.route.snapshot.queryParamMap.get('token') || '';

    constructor(
        private fb: NonNullableFormBuilder,
        private userService: UserService,
        private router: Router,
        private loaderService: LoaderService,
        private auth: Auth,
        private storageService: StorageService,
        private authService: AuthService,
        private route: ActivatedRoute,
        private dialog: MatDialog,
    ) {
        afterNextRender(() => {
            if (this.token) {
                this.handleFirstTimeLogin();
            }
        });
    }

    protected loginWithGoogle(): void {
        this.loginWithProvider(new GoogleAuthProvider(), 'google');
    }

    protected loginWithFacebook(): void {
        this.loginWithProvider(new FacebookAuthProvider(), 'facebook');
    }

    protected loginWithApple(): void {
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        this.loginWithProvider(provider, 'apple');
    }

    protected submit(): void {
        if (!this.authForm.valid) {
            return;
        }

        const email = this.authForm.value.email?.trim();
        this.loaderService.show();
        this.userService
            .checkEmailRegistered(email as string)
            .pipe(
                tap(() => {
                    this.router.navigate(['/login'], { queryParams: { email } });
                }),
                catchError((error) => {
                    if (error.error?.code === 'E400') {
                        this.router.navigate(['/signup'], { queryParams: { email } });
                    } else {
                        const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                        errorPopupConfig.data = {
                            title: `Error`,
                            message: error.error?.message,
                        };
                        this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                        console.error('Error checking email registered:', error);
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    private async loginWithProvider(
        provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider,
        type: 'google' | 'facebook' | 'apple',
    ): Promise<void> {
        const result = await signInWithPopup(this.auth, provider);
        const user = result.user;
        const name = user.displayName;
        const email = user.email;
        const idToken = await user.getIdToken();

        const payload: ILoginData = {
            firebaseUserId: user.uid,
            firebaseToken: idToken,
            type,
            accountId: user.uid,
            accountToken: idToken,
            deviceType: 'web',
            deviceId: this.storageService.getDeviceId() as string,
            appVersion: APP_VERSION,
            zoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        this.loaderService.show();
        this.authService
            .login(payload)
            .pipe(
                tap((response) => {
                    this.storageService.setLoginUser(response);
                    if (response.status === 'incomplete') {
                        if (name && email) {
                            this.completeUserProfile(name, email);
                        } else {
                            const popupConfig = EnterInfoPopupComponent.getDialogConfig();
                            popupConfig.data = { name: name || '', email: email || '' };
                            const dialogRef = this.dialog.open(EnterInfoPopupComponent, popupConfig);
                            dialogRef.afterClosed().subscribe((formValue) => {
                                if (!formValue) {
                                    return;
                                }
                                this.completeUserProfile(formValue.displayName, formValue.email);
                            });
                        }
                    } else {
                        this.router.navigate(['/community']);
                    }
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error logging in:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    private completeUserProfile(name: string, email: string): void {
        this.userService
            .completeUserProfile(name, email)
            .pipe(
                tap(() => {
                    this.storageService.updateNameEmail(name, email);
                    this.router.navigate(['/community']);
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error completing user profile:', error);
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    private handleFirstTimeLogin(): void {
        this.loaderService.show();
        this.authService
            .firstTimeLogin(this.token)
            .pipe(
                tap((response) => {
                    this.storageService.setLoginUser(response);
                    this.router.navigate(['/community']);
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error logging in:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }
}
